// SCR-STAFF-888 — حساب‌های بانکی (US-ACC-050): فهرست با شماره پوشیده برای نقش‌های غیرمالی، تعریف و ویرایش (بانک از داده مرجع، شعبه بانک،
// شماره حساب، شبا، نوع، شعبه مرکز) و تفصیلی BankAccount خودکار (AC-ACC-050.1).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type AccBankAccountKind, type AccBankAccountView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { accKeys, SCR_STAFF_888, useAccBranches, useBranchName } from './common';

const KINDS: readonly AccBankAccountKind[] = ['Current', 'Savings', 'ShortTermDeposit', 'Other'];

interface BankForm {
  bankCode: string;
  bankBranch: string;
  accountNumber: string;
  iban: string;
  kind: AccBankAccountKind;
  branchId: string;
  title: string;
  isActive: boolean;
}

const EMPTY: BankForm = {
  bankCode: '',
  bankBranch: '',
  accountNumber: '',
  iban: '',
  kind: 'Current',
  branchId: '',
  title: '',
  isActive: true,
};

export function BanksPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const branches = useAccBranches();
  const branchName = useBranchName();
  const canManage = access.accessMode === 'Full' && access.can('acc.bank.manage');
  const banks = useQuery({
    queryKey: accKeys.banks(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/bank-accounts')),
  });
  const [editing, setEditing] = useState<AccBankAccountView | 'new' | null>(null);
  const [form, setForm] = useState<BankForm>(EMPTY);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        bankCode: form.bankCode.trim(),
        bankBranch: form.bankBranch.trim() || null,
        accountNumber: form.accountNumber.trim(),
        iban: form.iban.trim() || null,
        kind: form.kind,
        branchId: form.branchId || null,
        title: form.title.trim(),
        isActive: form.isActive,
      };
      return editing === 'new' || editing === null
        ? unwrap(
            tenantApi.POST('/api/v1/accounting/bank-accounts', {
              body,
              headers: { 'Idempotency-Key': key.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/accounting/bank-accounts/{id}', {
              params: { path: { id: editing.id } },
              body,
            }),
          );
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) });
    },
  });
  const openEdit = (bank: AccBankAccountView | 'new') => {
    setForm(
      bank === 'new'
        ? EMPTY
        : {
            bankCode: bank.bankCode,
            bankBranch: bank.bankBranch ?? '',
            accountNumber: bank.isMasked ? '' : bank.accountNumber,
            iban: bank.isMasked ? '' : (bank.iban ?? ''),
            kind: bank.kind,
            branchId: bank.branchId ?? '',
            title: bank.title,
            isActive: bank.isActive,
          },
    );
    setEditing(bank);
  };
  const set = (patch: Partial<BankForm>) => setForm((current) => ({ ...current, ...patch }));
  const columns: DataTableColumn<AccBankAccountView>[] = [
    { id: 'title', headerKey: 'acc:bank.name', cell: (row) => row.title },
    { id: 'bank', headerKey: 'acc:bank.bank', cell: (row) => row.bankName ?? row.bankCode },
    {
      id: 'number',
      headerKey: 'acc:bank.account_number',
      cell: (row) => (
        <bdi dir="ltr" data-testid="acc-bank-number">
          {row.accountNumber}
        </bdi>
      ),
    },
    {
      id: 'iban',
      headerKey: 'acc:bank.iban',
      cell: (row) => (row.iban ? <bdi dir="ltr">{row.iban}</bdi> : '—'),
    },
    { id: 'kind', headerKey: 'acc:bank.kind', cell: (row) => t(`acc:bank_kind.${row.kind}`) },
    { id: 'branch', headerKey: 'acc:bank.center_branch', cell: (row) => branchName(row.branchId) ?? '—' },
    {
      id: 'detail',
      headerKey: 'acc:bank.detail',
      cell: (row) => (row.detailCode ? <bdi dir="ltr">{row.detailCode}</bdi> : '—'),
    },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => (
        <StatusChip
          tone={row.isActive ? 'active' : 'inactive'}
          labelKey={row.isActive ? 'acc:common.active' : 'acc:common.inactive'}
        />
      ),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => openEdit(row)} data-testid="acc-bank-edit">
            {t('acc:common.edit')}
          </Button>
        ) : null,
    },
  ];
  const masked = (banks.data?.items ?? []).some((bank) => bank.isMasked);
  return (
    <ListPage
      screenId={SCR_STAFF_888}
      titleKey="acc:bank.title"
      subtitleKey="acc:bank.subtitle"
      actions={
        canManage ? (
          <Button variant="contained" onClick={() => openEdit('new')} data-testid="acc-bank-add">
            {t('acc:bank.add')}
          </Button>
        ) : undefined
      }
    >
      {masked ? (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="acc-bank-masked">
          {t('acc:bank.masked_hint')}
        </Alert>
      ) : null}
      <DataTable
        captionKey="acc:bank.title"
        columns={columns}
        rows={banks.data?.items}
        getRowId={(row) => row.id}
        isLoading={banks.isPending}
        error={banks.error}
        onRetry={() => void banks.refetch()}
        emptyTitleKey="acc:bank.empty"
      />
      <FormDialog
        open={editing !== null}
        titleKey={editing === 'new' ? 'acc:bank.add' : 'acc:bank.edit'}
        onSubmit={() => save.mutate()}
        onClose={() => setEditing(null)}
        isDirty={form.title !== '' || form.accountNumber !== ''}
        isSubmitting={save.isPending}
        submitError={save.error}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('acc:bank.name')}
            value={form.title}
            required
            onChange={(event) => set({ title: event.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'acc-bank-title' } }}
          />
          <TextField
            label={t('acc:bank.bank')}
            value={form.bankCode}
            required
            onChange={(event) => set({ bankCode: event.target.value })}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'acc-bank-code' } }}
          />
          <TextField
            label={t('acc:bank.bank_branch')}
            value={form.bankBranch}
            onChange={(event) => set({ bankBranch: event.target.value })}
          />
          <TextField
            label={t('acc:bank.account_number')}
            value={form.accountNumber}
            required
            onChange={(event) => set({ accountNumber: event.target.value })}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'acc-bank-account' } }}
          />
          <TextField
            label={t('acc:bank.iban')}
            value={form.iban}
            onChange={(event) => set({ iban: event.target.value })}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'acc-bank-iban' } }}
          />
          <TextField
            select
            label={t('acc:bank.kind')}
            value={form.kind}
            onChange={(event) => set({ kind: event.target.value as AccBankAccountKind })}
          >
            {KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {t(`acc:bank_kind.${kind}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('acc:bank.center_branch')}
            value={form.branchId}
            onChange={(event) => set({ branchId: event.target.value })}
          >
            <MenuItem value="">{t('acc:common.all')}</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isActive}
                onChange={(event) => set({ isActive: event.target.checked })}
              />
            }
            label={t('acc:common.active')}
          />
        </Stack>
      </FormDialog>
    </ListPage>
  );
}
